/**
 * Service de logging centralisé
 *
 * Permet de gérer les logs de manière uniforme avec différents niveaux.
 * En production, seuls les logs de niveau 'warn' et 'error' sont affichés.
 * En développement, tous les niveaux sont affichés.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  data?: unknown;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// En production, on ne log que warn et error
const MIN_LEVEL: LogLevel = import.meta.env.PROD ? 'warn' : 'debug';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LEVEL];
}

function formatMessage(entry: LogEntry): string {
  const timestamp = entry.timestamp.toISOString();
  const context = entry.context ? `[${entry.context}]` : '';
  return `${timestamp} ${entry.level.toUpperCase()} ${context} ${entry.message}`;
}

function logToConsole(entry: LogEntry): void {
  if (!shouldLog(entry.level)) return;

  const message = formatMessage(entry);
  const args = entry.data ? [message, entry.data] : [message];

  switch (entry.level) {
    case 'debug':
      console.debug(...args);
      break;
    case 'info':
      console.info(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
    case 'error':
      console.error(...args);
      break;
  }
}

/**
 * Créer un logger avec un contexte spécifique
 *
 * @example
 * const log = createLogger('AuthService');
 * log.info('User logged in', { userId: '123' });
 * log.error('Login failed', { error: err.message });
 */
export function createLogger(context: string) {
  return {
    debug: (message: string, data?: unknown) => {
      logToConsole({ level: 'debug', message, timestamp: new Date(), context, data });
    },
    info: (message: string, data?: unknown) => {
      logToConsole({ level: 'info', message, timestamp: new Date(), context, data });
    },
    warn: (message: string, data?: unknown) => {
      logToConsole({ level: 'warn', message, timestamp: new Date(), context, data });
    },
    error: (message: string, data?: unknown) => {
      logToConsole({ level: 'error', message, timestamp: new Date(), context, data });
    },
  };
}

/**
 * Logger global sans contexte
 */
export const logger = {
  debug: (message: string, data?: unknown) => {
    logToConsole({ level: 'debug', message, timestamp: new Date(), data });
  },
  info: (message: string, data?: unknown) => {
    logToConsole({ level: 'info', message, timestamp: new Date(), data });
  },
  warn: (message: string, data?: unknown) => {
    logToConsole({ level: 'warn', message, timestamp: new Date(), data });
  },
  error: (message: string, data?: unknown) => {
    logToConsole({ level: 'error', message, timestamp: new Date(), data });
  },
};

export type Logger = ReturnType<typeof createLogger>;
